import type { FFmpeg } from '@ffmpeg/ffmpeg';

import { fetchFileAsBytes } from '../../../utils/downloadFile';
import { MaTelopFont } from './maTelopFonts';
import { MaSegment, maSegmentDisplaySeconds } from './maTypes';
import { RenderMaSegmentResult, renderMaSegmentToWav } from './renderMaSegment';

/**
 * MA セグメントの動画レンダリング書き出し。
 *
 * ffmpeg.wasm（シングルスレッド版）で、音声トラックを renderMaSegmentToWav の
 * フルミックスに差し替えた動画を生成する。2 つのモードがある:
 *
 * - remux（既定）: 元動画の映像ストリームを無劣化コピーし音声だけ差し替える。
 *   高速・無劣化で、テール（動画より長い音声）もそのまま格納される。
 *   映像を触らないため単一の動画区間（videoSpans.length === 1）に限る
 * - telop: タイムチャート（ASS 字幕）を焼き込んで libx264 で再エンコードする。
 *   複数の動画区間（オプション候補の動画切替）は concat で連結する。
 *   フォントは maTelopFonts.ts の OTF を仮想 FS の /fonts に載せて
 *   `subtitles=...:fontsdir=/fonts` で参照する（libass はシステムフォントを見ない）
 *
 * - 出力コンテナは元ファイルに追従: mov → mov（音声 PCM 24bit）/ それ以外 → mp4（音声 AAC 320k）
 * - コア（約 31MB）は初回のみ遅延ロードし、モジュールスコープで再利用する
 * - シングルスレッド版は SharedArrayBuffer 不要（COOP/COEP ヘッダー変更なしで動作）
 */

/** ffmpeg.wasm の仮想 FS に載せる元動画の上限（wasm 2GB メモリ制限を考慮。複数区間は合計） */
const MAX_VIDEO_BYTES = 800 * 1024 * 1024;

/** 仮想 FS 上のフォント配置先 */
const FONTS_DIR = '/fonts';

export type RenderVideoPhase = 'load' | 'mix' | 'fetch' | 'mux' | 'encode';

export type RenderVideoMode = 'remux' | 'telop';

export interface RenderVideoTelopOptions {
  /**
   * ASS 字幕の生成。動画の実サイズ（PlayRes）が分かった時点で呼ばれる
   * （buildMaTelopAssForSegment を部分適用して渡す）
   */
  buildAss: (_frame: { width: number; height: number }) => string;
  /** 焼き込みに使うフォント */
  font: MaTelopFont;
}

export interface RenderVideoOptions {
  /** 既定 'remux' */
  mode?: RenderVideoMode;
  /** mode = 'telop' のときに必須 */
  telop?: RenderVideoTelopOptions;
  /** telop モードで 720p に縮小して高速化（元が 720p 以下ならそのまま） */
  scaleTo720p?: boolean;
  /** 複数区間の連結で揃える fps（シーケンス fps。単一区間では未使用） */
  fps?: number;
  /**
   * 事前レンダリング済みのミックス（WAV と動画を同時に書き出すときの
   * 二重レンダリング回避）。未指定なら内部で renderMaSegmentToWav を呼ぶ
   */
  mix?: RenderMaSegmentResult;
  /** 動画バイト列のキャッシュ（メディアアイテム ID → bytes）。同一動画の多重取得を避ける */
  videoBytesCache?: Map<string, Uint8Array>;
  /** フェーズ遷移の通知（トースト文言の切替用） */
  onPhase?: (_phase: RenderVideoPhase) => void;
  /** ffmpeg 実行（mux / encode フェーズ）の進捗（0-1）。remux は高速なため粗い粒度で届く */
  onRenderProgress?: (_ratio: number) => void;
}

export interface RenderVideoResult {
  blob: Blob;
  /** 保存ファイルの拡張子（元コンテナに追従） */
  extension: 'mp4' | 'mov';
  /** 音声をデコードできず除外したファイル名（renderMaSegmentToWav 由来） */
  skippedFiles: string[];
}

// ===== ffmpeg シングルトン（遅延ロード） =====

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoading: Promise<FFmpeg> | null = null;

async function getFfmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;
  if (!ffmpegLoading) {
    ffmpegLoading = (async () => {
      // メインバンドルを重くしないため実行時に動的 import する
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const ffmpeg = new FFmpeg();
      // コアは vite-plugin-static-copy で自オリジンの /ffmpeg/ に配置している。
      // worker 内の動的 import で解決されるため絶対 URL で渡す。
      // BASE_URL は設定値そのままのため末尾スラッシュを正規化する
      const rawBase = import.meta.env.BASE_URL || '/';
      const base = new URL(
        `${rawBase.endsWith('/') ? rawBase : `${rawBase}/`}ffmpeg/`,
        window.location.origin,
      );
      await ffmpeg.load({
        coreURL: new URL('ffmpeg-core.js', base).href,
        wasmURL: new URL('ffmpeg-core.wasm', base).href,
      });
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    })().catch(e => {
      // 失敗時は次回クリックで再ロードできるようにする
      ffmpegLoading = null;
      throw e;
    });
  }
  return ffmpegLoading;
}

// ===== フォント（仮想 FS への配置） =====

/** フォントファイルのバイト列キャッシュ（key → bytes。ページ内で 1 回だけ取得） */
const fontBytesCache = new Map<string, Promise<Uint8Array>>();

/** 仮想 FS に書き込み済みのフォント（ffmpeg インスタンスごと） */
const fontsInFs = new WeakMap<FFmpeg, Set<string>>();

async function fetchFontBytes(font: MaTelopFont): Promise<Uint8Array> {
  let pending = fontBytesCache.get(font.key);
  if (!pending) {
    pending = (async () => {
      const res = await fetch(font.url);
      if (!res.ok) throw new Error(`テロップ用フォントを取得できませんでした（${font.label}）`);
      return new Uint8Array(await res.arrayBuffer());
    })().catch(e => {
      fontBytesCache.delete(font.key);
      throw e;
    });
    fontBytesCache.set(font.key, pending);
  }
  return pending;
}

/** フォントを仮想 FS の /fonts に配置する（初回のみ） */
async function ensureFontInFs(ffmpeg: FFmpeg, font: MaTelopFont): Promise<void> {
  let loaded = fontsInFs.get(ffmpeg);
  if (!loaded) {
    loaded = new Set();
    fontsInFs.set(ffmpeg, loaded);
    try {
      await ffmpeg.createDir(FONTS_DIR);
    } catch {
      /* 作成済みなら無視 */
    }
  }
  if (loaded.has(font.key)) return;
  const bytes = await fetchFontBytes(font);
  // writeFile は buffer を transfer（detach）するため、キャッシュ側はコピーを渡して保護する
  await ffmpeg.writeFile(`${FONTS_DIR}/${font.fileName}`, bytes.slice());
  loaded.add(font.key);
}

// ===== 動画サイズの取得 =====

/** 動画バイト列の実サイズを video 要素のメタデータから取得する（失敗時は null） */
function probeVideoFrameSize(
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ width: number; height: number } | null> {
  return new Promise(resolve => {
    let url: string;
    try {
      // Blob はビューの範囲だけを取り込む（TS の ArrayBufferLike 判定を回避するためのキャスト）
      url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mimeType }));
    } catch {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const done = (size: { width: number; height: number } | null) => {
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(size);
    };
    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      done(width > 0 && height > 0 ? { width, height } : null);
    };
    video.onerror = () => done(null);
    video.src = url;
  });
}

// ===== ffmpeg 引数の組み立て（純関数・テスト対象） =====

export interface MuxArgsOptions {
  /** 仮想 FS 上の元動画ファイル名 */
  videoFile: string;
  /** 仮想 FS 上のミックス WAV ファイル名 */
  audioFile: string;
  /** 仮想 FS 上の出力ファイル名 */
  outputFile: string;
  /** 動画ファイル内での表示開始位置（秒）。0 なら先頭から */
  videoOffsetSeconds: number;
  /** 明示 end による表示尺（秒）。null なら動画末尾まで */
  videoTrimSeconds: number | null;
  /** 出力コンテナ */
  container: 'mp4' | 'mov';
}

/** コンテナごとの音声エンコード引数 */
function audioCodecArgs(container: 'mp4' | 'mov'): string[] {
  return container === 'mov' ? ['-c:a', 'pcm_s24le'] : ['-c:a', 'aac', '-b:a', '320k'];
}

/**
 * リマックス用の ffmpeg 引数を組み立てる。
 *
 * -ss / -t は入力オプションとして動画側にのみ適用する（copy のため
 * キーフレーム単位のカットになる）。-t を出力側に置くと音声テールまで
 * 切れてしまうため、必ず動画入力の前に置くこと。
 */
export function buildMuxArgs(options: MuxArgsOptions): string[] {
  const args: string[] = [];

  if (options.videoOffsetSeconds > 0) {
    args.push('-ss', String(options.videoOffsetSeconds));
  }
  if (options.videoTrimSeconds != null) {
    args.push('-t', String(options.videoTrimSeconds));
  }
  args.push('-i', options.videoFile);
  args.push('-i', options.audioFile);
  args.push('-map', '0:v:0', '-map', '1:a:0');
  args.push('-c:v', 'copy');
  args.push(...audioCodecArgs(options.container));
  if (options.container === 'mp4') {
    // mp4 は moov 先頭化でストリーミング再生に対応
    args.push('-movflags', '+faststart');
  }
  args.push('-y', options.outputFile);
  return args;
}

/** テロップ焼き込み用の動画入力 1 区間 */
export interface TelopVideoInput {
  /** 仮想 FS 上のファイル名 */
  file: string;
  /** ファイル内の開始位置（秒） */
  offsetSeconds: number;
  /** 区間の尺（秒）。null は末尾まで（最終区間のみ） */
  durationSeconds: number | null;
}

export interface TelopEncodeArgsOptions {
  /** 動画区間（表示順）。2 区間以上は concat で連結する */
  videoInputs: TelopVideoInput[];
  audioFile: string;
  /** 仮想 FS 上の ASS ファイル名 */
  assFile: string;
  /** 仮想 FS 上のフォントディレクトリ */
  fontsDir: string;
  outputFile: string;
  container: 'mp4' | 'mov';
  scaleTo720p: boolean;
  /** 先頭区間の動画サイズ（複数区間の連結で揃える基準） */
  frameSize: { width: number; height: number };
  /** 複数区間の連結で揃える fps */
  fps: number;
}

/** 偶数に丸める（yuv420p は奇数サイズを受け付けない） */
function even(n: number): number {
  const r = Math.round(n);
  return r % 2 === 0 ? r : r - 1;
}

/**
 * テロップ焼き込み（再エンコード）用の ffmpeg 引数を組み立てる。
 *
 * - 各動画区間は `-ss` / `-t` を入力オプションにして正確に切り出す（再エンコードのため
 *   フレーム単位で正確）。`-t` を出力側に置かないのは remux と同じ（音声テール保護）
 * - 単一区間: `[0:v][scale,]subtitles[vout]`
 * - 複数区間: 各入力を先頭区間のサイズ・fps に揃えてから concat し、字幕を乗せる
 *   （concat は解像度・fps・ピクセルフォーマットの一致が必要）
 * - 720p 縮小は単一区間では式（元が 720p 以下なら等倍）、複数区間では揃えるサイズ自体を縮める
 * - libx264 ultrafast / crf 23 / yuv420p。wasm シングルスレッドのため速度優先
 */
export function buildTelopEncodeArgs(options: TelopEncodeArgsOptions): string[] {
  const { videoInputs, audioFile, assFile, fontsDir, outputFile, container, scaleTo720p } =
    options;
  if (videoInputs.length === 0) throw new Error('動画区間がありません');

  const args: string[] = [];
  videoInputs.forEach(input => {
    if (input.offsetSeconds > 0) args.push('-ss', String(input.offsetSeconds));
    if (input.durationSeconds != null) args.push('-t', String(input.durationSeconds));
    args.push('-i', input.file);
  });
  args.push('-i', audioFile);
  const audioIndex = videoInputs.length;

  const subtitles = `subtitles=${assFile}:fontsdir=${fontsDir}`;
  let graph: string;
  if (videoInputs.length === 1) {
    const chain = [
      // 幅 -2 でアスペクト維持 + 偶数化。高さは 720 と元の小さい方（拡大しない）
      ...(scaleTo720p ? ["scale=-2:'min(ih,720)'"] : []),
      subtitles,
    ];
    graph = `[0:v]${chain.join(',')}[vout]`;
  } else {
    const { width, height } = options.frameSize;
    const targetH = scaleTo720p ? Math.min(height, 720) : height;
    const targetW = even((width * targetH) / height);
    const th = even(targetH);
    const normalize =
      `scale=${targetW}:${th}:force_original_aspect_ratio=decrease,` +
      `pad=${targetW}:${th}:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=${options.fps}`;
    const normalized = videoInputs.map((_, i) => `[${i}:v]${normalize}[v${i}]`);
    const labels = videoInputs.map((_, i) => `[v${i}]`).join('');
    graph = [
      ...normalized,
      `${labels}concat=n=${videoInputs.length}:v=1:a=0[vcat]`,
      `[vcat]${subtitles}[vout]`,
    ].join(';');
  }

  args.push('-filter_complex', graph);
  args.push('-map', '[vout]', '-map', `${audioIndex}:a:0`);
  args.push('-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '23', '-pix_fmt', 'yuv420p');
  args.push(...audioCodecArgs(container));
  if (container === 'mp4') args.push('-movflags', '+faststart');
  args.push('-y', outputFile);
  return args;
}

/** 元ファイル名から出力コンテナを決める（mov は映像コーデックを保てる mov のまま） */
export function pickOutputContainer(fileName: string): 'mp4' | 'mov' {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return ext === 'mov' || ext === 'qt' ? 'mov' : 'mp4';
}

/** 仮想 FS 上の入力ファイル名（拡張子は元ファイルに追従、不明は mp4） */
function inputFileName(index: number, originalName: string): string {
  const ext = originalName.split('.').pop()?.toLowerCase() ?? '';
  const safe = /^[a-z0-9]{1,5}$/.test(ext) ? ext : 'mp4';
  return `input_${index}.${safe}`;
}

/**
 * 区間ごとの尺（秒）。中間区間は次区間の開始まで、最終区間は表示尺が確定している
 * 場合（明示 end / 候補動画による延長）のみ切り、そうでなければ末尾まで
 */
export function spanDurations(segment: MaSegment): Array<number | null> {
  const spans = segment.videoSpans;
  const display = maSegmentDisplaySeconds(segment);
  const trimLast = segment.hasExplicitEnd || segment.resolvedDisplaySeconds != null;
  return spans.map((span, i) => {
    const next = spans[i + 1];
    if (next) return Math.max(0, next.startSeconds - span.startSeconds);
    if (trimLast && display != null) return Math.max(0, display - span.startSeconds);
    return null;
  });
}

// ===== 本体 =====

export async function renderMaSegmentToVideo(
  segment: MaSegment,
  options: RenderVideoOptions = {},
): Promise<RenderVideoResult> {
  const {
    mode = 'remux',
    telop,
    scaleTo720p = false,
    fps = 30,
    mix,
    videoBytesCache,
    onPhase,
    onRenderProgress,
  } = options;

  // remux は映像を無劣化コピーするため、複数の動画表示区間（オプション候補の
  // 動画切替）は再エンコードが必要になり対応しない。telop モードを案内する
  if (mode === 'remux' && segment.videoSpans.length > 1) {
    throw new Error(
      'オプション候補の動画切替を含むため、ミックス動画（無劣化）は書き出せません。タイムチャート付き動画をご利用ください',
    );
  }
  if (mode === 'telop' && !telop) {
    throw new Error('テロップ設定が指定されていません');
  }

  // コアのダウンロードはミックスレンダリングと並行で開始しておく。
  // 'load' フェーズの通知は実際にロードが必要な初回のみ
  if (!ffmpegInstance) onPhase?.('load');
  const ffmpegPromise = getFfmpeg();

  // 1. 再生と同一のフルミックスを WAV にレンダリング（事前レンダリング済みなら再利用）
  let mixResult = mix;
  if (!mixResult) {
    onPhase?.('mix');
    mixResult = await renderMaSegmentToWav(segment);
  }
  const { blob: mixWav, skippedFiles } = mixResult;

  // 2. 元動画の取得（区間ごとのユニークなアイテム）。
  // ffmpeg へはバイト列で渡すため Blob を経由しない（fetchFileAsBytes）
  onPhase?.('fetch');
  const spans = segment.videoSpans;
  const bytesByItemId = new Map<string, Uint8Array>();
  let totalBytes = 0;
  for (const span of spans) {
    const item = span.videoItem;
    if (bytesByItemId.has(item.id)) continue;
    let bytes = videoBytesCache?.get(item.id);
    if (!bytes) {
      bytes = await fetchFileAsBytes(item.original_url);
      videoBytesCache?.set(item.id, bytes);
    }
    bytesByItemId.set(item.id, bytes);
    totalBytes += bytes.byteLength;
  }
  if (totalBytes > MAX_VIDEO_BYTES) {
    throw new Error(
      `動画ファイルが大きすぎるため書き出せません（${Math.round(totalBytes / 1024 / 1024)}MB）。` +
        'WAV 書き出しをご利用ください',
    );
  }

  const ffmpeg = await ffmpegPromise;

  // 3. 仮想 FS のファイル名を決める
  const container = pickOutputContainer(spans[0].videoItem.file_name);
  const outputFile = `output.${container}`;
  const audioFile = 'mix.wav';
  const assFile = 'telop.ass';
  const fileByItemId = new Map<string, string>();
  Array.from(bytesByItemId.keys()).forEach((id, i) => {
    const item = spans.find(s => s.videoItem.id === id)?.videoItem;
    fileByItemId.set(id, inputFileName(i, item?.file_name ?? ''));
  });

  let args: string[];
  if (mode === 'telop' && telop) {
    // テロップ: 実サイズで ASS を生成し、フォントを仮想 FS に配置してから引数を組む
    const firstBytes = bytesByItemId.get(spans[0].videoItem.id) as Uint8Array;
    const frameSize = (await probeVideoFrameSize(
      firstBytes,
      container === 'mov' ? 'video/quicktime' : 'video/mp4',
    )) ?? { width: 1920, height: 1080 };
    await ensureFontInFs(ffmpeg, telop.font);
    await ffmpeg.writeFile(assFile, telop.buildAss(frameSize));
    const durations = spanDurations(segment);
    args = buildTelopEncodeArgs({
      videoInputs: spans.map((span, i) => ({
        file: fileByItemId.get(span.videoItem.id) as string,
        offsetSeconds: span.fileOffsetSeconds,
        durationSeconds: durations[i],
      })),
      audioFile,
      assFile,
      fontsDir: FONTS_DIR,
      outputFile,
      container,
      scaleTo720p,
      frameSize,
      fps,
    });
  } else {
    args = buildMuxArgs({
      videoFile: fileByItemId.get(spans[0].videoItem.id) as string,
      audioFile,
      outputFile,
      videoOffsetSeconds: spans[0].fileOffsetSeconds,
      // 明示 end がある場合のみ動画をカットする（通常は末尾まで格納）。
      // カット位置は候補による延長込みの実効表示尺
      videoTrimSeconds: segment.hasExplicitEnd ? maSegmentDisplaySeconds(segment) : null,
      container,
    });
  }

  // 4. 実行
  onPhase?.(mode === 'telop' ? 'encode' : 'mux');

  // 失敗時の原因調査用に直近のログを保持する
  const recentLogs: string[] = [];
  const fontLogs: string[] = [];
  const handleLog = ({ message }: { message: string }) => {
    recentLogs.push(message);
    if (recentLogs.length > 40) recentLogs.shift();
    if (/fontselect|Loading font file|font provider/i.test(message)) fontLogs.push(message);
  };
  const handleProgress = ({ progress }: { progress: number }) => {
    if (Number.isFinite(progress)) {
      onRenderProgress?.(Math.max(0, Math.min(1, progress)));
    }
  };
  ffmpeg.on('log', handleLog);
  ffmpeg.on('progress', handleProgress);

  const tempFiles = [...fileByItemId.values(), audioFile, outputFile, assFile];
  try {
    for (const [id, file] of fileByItemId) {
      const bytes = bytesByItemId.get(id) as Uint8Array;
      // writeFile は ArrayBuffer を worker へ transfer（detach）するため、
      // キャッシュに残す場合はコピーを渡す（次のジョブで再利用できるように）
      await ffmpeg.writeFile(file, videoBytesCache ? bytes.slice() : bytes);
    }
    await ffmpeg.writeFile(audioFile, new Uint8Array(await mixWav.arrayBuffer()));

    const code = await ffmpeg.exec(args);
    if (code !== 0) {
      console.warn('[MA] ffmpeg 書き出し失敗ログ:', recentLogs.join('\n'));
      throw new Error(`動画の書き出しに失敗しました（ffmpeg exit code ${code}）`);
    }
    if (mode === 'telop' && fontLogs.length > 0) {
      // fontsdir のフォントが選ばれているかの確認用（fontselect: (name) -> file）
      console.debug('[MA] テロップのフォント解決:', fontLogs.join('\n'));
    }

    const data = await ffmpeg.readFile(outputFile);
    if (typeof data === 'string') throw new Error('unexpected ffmpeg output');
    // SharedArrayBuffer 由来の可能性があるため通常の ArrayBuffer へコピーする
    const bytes = new Uint8Array(data);
    const blob = new Blob([bytes], {
      type: container === 'mov' ? 'video/quicktime' : 'video/mp4',
    });
    return { blob, extension: container, skippedFiles };
  } finally {
    ffmpeg.off('log', handleLog);
    ffmpeg.off('progress', handleProgress);
    // 仮想 FS を後始末してメモリを解放する（フォントは次回のため残す）
    for (const file of tempFiles) {
      try {
        await ffmpeg.deleteFile(file);
      } catch {
        /* 未作成なら無視 */
      }
    }
  }
}
